/**
 * MESSAGES LINK COMPONENT
 * 
 * Shows "Messages" text link with unread count badge.
 * Plays a notification sound when new messages arrive.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Conversation } from '../messaging/types';

interface MessagesLinkProps {
  userId: string | null;
  className?: string;
  onClick?: () => void;
}

// Play message notification sound using mp3 file
const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Audio play failed (user hasn't interacted with page yet)
    });
  } catch {
    // Audio not supported
  }
};

const MessagesLink: React.FC<MessagesLinkProps> = ({ userId, className = '', onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const previousCountRef = useRef<number | null>(null); // null = not initialized yet
  const soundEnabledRef = useRef(false);

  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    // Reset refs on mount
    previousCountRef.current = null;
    soundEnabledRef.current = false;

    // Enable sound after 3 seconds to let initial data settle
    const soundTimer = setTimeout(() => {
      soundEnabledRef.current = true;
    }, 3000);

    // Subscribe to conversations where user is a participant
    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', userId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let count = 0;
      
      snapshot.docs.forEach((doc) => {
        const data = doc.data() as Conversation;
        
        // Count as unread if:
        // 1. There's a last message
        // 2. The last message wasn't sent by the current user
        // 3. The last message timestamp is after user's lastReadAt (or user has never read)
        if (data.lastMessage && data.lastMessage.senderId !== userId) {
          const lastReadAt = data.lastReadAt?.[userId];
          const messageTimestamp = data.lastMessage.timestamp;
          
          // If user has never read this conversation, or message is newer than last read
          if (!lastReadAt || !messageTimestamp) {
            count++;
          } else {
            const lastReadMs = lastReadAt.toMillis?.() || 0;
            const messageMs = messageTimestamp.toMillis?.() || 0;
            if (messageMs > lastReadMs) {
              count++;
            }
          }
        }
      });
      
      // Play sound ONLY if:
      // 1. Sound is enabled (after initial delay)
      // 2. We have a previous count to compare against
      // 3. Count actually increased
      // 4. User is NOT currently on a messages page
      if (
        soundEnabledRef.current && 
        previousCountRef.current !== null && 
        count > previousCountRef.current &&
        !window.location.pathname.startsWith('/messages')
      ) {
        playNotificationSound();
      }
      
      previousCountRef.current = count;
      setUnreadCount(count);
    }, (error) => {
      console.error('Error listening to conversations:', error);
    });

    return () => {
      clearTimeout(soundTimer);
      unsubscribe();
    };
  }, [userId]);

  // Don't render if not logged in
  if (!userId) return null;

  return (
    <Link 
      to="/messages" 
      onClick={onClick}
      className={`relative transition-colors ${className}`}
      style={{ fontFamily: 'Nunito, sans-serif' }}
    >
      Messages
      {unreadCount > 0 && (
        <span className="absolute -top-2 -right-3 badge-count">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Link>
  );
};

export default MessagesLink;
